import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import clone from 'lodash/clone';
import { JsonHelper } from './jsonHelper';
import { getRandomString } from '../../../Utils/math';

/**
 * @ignore Property Rights
 */

export class JsonCorrecter extends JsonHelper {
    threedJson: any

    public correct(threedJson: any): void {
        this.threedJson = threedJson;
        this.correctPlugins();
        this.correctContents();
        this.correctPointofviews();
    }

    private correctPlugins() {
        // 18/04/2020
        // Now we have general pipeline parameters
        if (!this.threedJson.pipeline) this.threedJson.pipeline = {};
        // 28/05/2020
        // ColorMapping managed in one array
        if (this.threedJson.pipeline.coh !== undefined) {
            this.threedJson.pipeline.cm = [
                this.threedJson.pipeline.coh - 180,
                this.threedJson.pipeline.cos,
                this.threedJson.pipeline.coe,
                this.threedJson.pipeline.cod
            ];
        }

        // 28/01/2021
        // Add default initial vue
        if (!this.threedJson.camera.iv) {
            this.threedJson.camera.iv = {
                p: { x: 0, y: 2.5, z: 0 },
                r: { x: 40, y: 180, z: 0 },
                ra: 8,
            };
        }

        if (!this.threedJson.loader) this.threedJson.loader = {};
    }

    private correctContents() {
        let { contents } = this.threedJson;
        if (!this.threedJson.materials) this.threedJson.materials = [];
        if (!contents) {
            contents = {};
        } else {
            this.correctContentsType();
            // Need to create animation at the if animation has a target
            this.correctContentsAnimations();
        }
    }

    // 27/09/2020
    private fixMaterials = () => {
        const { materials, version } = this.threedJson;
        if (version) {
            const verionNumber = parseFloat(version.replaceAll('.', ''));
            if (verionNumber < 141) {
                materials.forEach((material) => {
                    const tintColor = material.mtc;
                    if (tintColor) {
                        const newA = [];
                        newA[0] = Math.floor(((tintColor[0] / 255) ** (1 / 2.2)) * 255);
                        newA[1] = Math.floor(((tintColor[1] / 255) ** (1 / 2.2)) * 255);
                        newA[2] = Math.floor(((tintColor[2] / 255) ** (1 / 2.2)) * 255);
                        material.mtc = newA;
                    }
                });
            }
        }
    }

    private correctContentsType() {
        let { contents } = this.threedJson;
        // 05/05/2022
        // Change the way we manage materials
        this.addMaterialKeys();

        // 22/08/2020
        // Contents can be saved as array so we put it to object
        if (contents.length !== undefined) {
            const newContents = {};
            contents.forEach((content) => {
                const key = content.i;
                newContents[key] = content;
            });
            contents = newContents;
        }

        const keys = Object.keys(contents);
        keys.forEach((key) => {
            const contentdata = contents[key];
            if (contentdata.tp === 'mesh') contentdata.tp = 'shape';
            // 06/04/20221
            // We stop using shapes
            if (contentdata.tp === 'shape') delete contents[key];

            if (contentdata.tp === 'light') {
                if (!contentdata.mo) contents[key].mo = 'point';
                if (contentdata.ch === undefined) contents[key].ch = true;
                if (contentdata.sd === undefined) contents[key].sd = 0;
            }
            if (contentdata.tp === 'model') {
                if (contentdata.c) contents[key].ma = { mabc: contentdata.c };
                // 05/04/2021
                // Change the way we manage combo and variant
                if (contentdata.co) {
                    const materialList = [];
                    contentdata.co.me.forEach((meshName) => {
                        materialList.push({ me: meshName, ma: contentdata.co.ma });
                    });
                    contents[key].va = { n: 'Variant 1', co: materialList };
                }
                // 05/10/2021
                // Change the way we manage combos
                if (contentdata.combos) {
                    const meshTest = [];
                    const variantMeshesMaterialsList = [];
                    contentdata.combos.forEach((combo) => {
                        const meshName = combo.me;
                        const material = combo.ma;
                        if (meshTest.includes(meshName)) {
                            const variantMeshesMaterials = find(
                                variantMeshesMaterialsList,
                                (c) => isEqual(c.me, [meshName])
                            );
                            variantMeshesMaterials.ma.push(material);
                        } else {
                            variantMeshesMaterialsList.push({ me: [meshName], ma: [material] });
                            meshTest.push(meshName);
                        }
                    });
                    contentdata.cmm = variantMeshesMaterialsList;
                }

                // 05/05/2022
                // Change the way we manage materials
                this.getMaterialListKeys(contentdata.cmm);

                // 05/10/2021
                // Every variation is saved in content "variations" key
                // so we only need the variation name here
                if (contents[key].va && contents[key].va.n) contents[key].va = contents[key].va.n;
                // 05/10/2021
                // Variations now managed in an array
                if (contentdata.variations) {
                    const variationsArray = [];
                    const keys = Object.keys(contentdata.variations);
                    keys.forEach((key) => {
                        const variation = contentdata.variations[key];
                        variationsArray.push(variation);
                    });
                    contentdata.vas = variationsArray;
                }
                // 28/10/2021
                // Variant key used instead of variation
                // But no need to correct it as "vas" is not changed
                // 16/10/2021
                // VariantModel now uses VariantMeshes instead of VariantMesh for more flexibility
                if (contentdata.vas) {
                    contentdata.vas.forEach((variant) => {
                        variant.co.forEach((variantMeshes) => {
                            if (typeof variantMeshes.me === 'string') variantMeshes.me = [variantMeshes.me];
                            // 05/05/2022
                            // Change the way we manage materials
                            // const materialKey = getMaterialKey(variantMeshes.ma, materials);
                            // if (materialKey) variantMeshes.ma = materialKey;
                            variantMeshes.ma = this.getMaterialKey(variantMeshes.ma);
                        });
                    });
                }

                // 05/05/2022
                // Change the way we manage materials
                contentdata.ma = this.getMaterialKey(contentdata.ma);
            }
        });

        //! FIX Materials avec isEqual is made between
        this.fixMaterials();

        return contents;
    }

    // 05/05/2022
    // Change the way we manage materials
    private addMaterialKeys() {
        const { materials } = this.threedJson;
        materials.forEach((material) => {
            if (!material.key) material.key = getRandomString();
        });
    }

    private getMaterialKey(material): string {
        if (!material || typeof material === 'string') return material;
        const { materials } = this.threedJson;
        const materialProject = find(materials, (m) => {
            const cleanMaterial = clone(m);
            delete cleanMaterial.key;
            return isEqual(cleanMaterial, material);
        });
        if (materialProject) {
            return materialProject.key;
        }
        materials.push(material);
        if (!material.key) material.key = getRandomString();
        return material.key;
    }

    private getMaterialListKeys(variantArray) {
        variantArray.forEach((combo) => {
            const comboMaterials = combo.ma;
            if (comboMaterials) {
                const materialKeyList = [];
                comboMaterials.forEach((material) => {
                    const materialKey = this.getMaterialKey(material);
                    if (materialKey) materialKeyList.push(materialKey);
                });
                combo.ma = materialKeyList;
            }
        });
    }

    private correctContentsAnimations() {
        const { contents } = this.threedJson;
        const keys = Object.keys(contents);
        keys.forEach((key) => {
            const contentdata = contents[key];
            if (contentdata.animations !== undefined) {
                const keys = Object.keys(contentdata.animations);
                keys.forEach((animkey) => {
                    const anim = contentdata.animations[animkey];
                    if (anim.ao === undefined) contentdata.animations[animkey].ao = 0;
                    if (anim.ade === undefined) contentdata.animations[animkey].ade = 0;
                    if (anim.ac === undefined) contentdata.animations[animkey].ac = 'linear';
                    if (anim.adu === undefined) contentdata.animations[animkey].adu = 1000000000000;
                    // 28/10/2020
                    // Better to use mouse event word instead of move which means nothing/everything
                    if (anim.ae === 'move') contentdata.animations[animkey].ae = 'mouse';
                });
            }
        });
    }

    private correctPointofviews() {
        let { pointofviews } = this.threedJson;
        if (!pointofviews) {
            pointofviews = {};
        } else {
            // 18/04/2020 pipeline can be set in environment
            const defaultPipelineValues = {
                f: 0.8,
                v: [0, 0, 0, 0],
                fc: [45, 44, 58, 0],
                vo: 0,
                fl: 0,
                fd: 1,
                b: 0,
                c: 1,
                e: 1,
                she: 0,
                shc: 1,
                ch: 0,
            };

            const environmentSkyValue = {
                sc: [255, 255, 255, 1],
                sbr: [0, 0, 0, 0],
                scbr: 1,
                sor: 1,
                sbl: 0,
                fc: [45, 44, 58, 0],
            };

            const keys = Object.keys(pointofviews);
            keys.forEach((key) => {
                const povdata = pointofviews[key];
                // 17/06/2020
                // bug where project was saved witout pipeline, add this security
                if (!povdata.pip) povdata.pip = {};
                // 14/04/2020
                // Merge pipeline and environment options
                if (typeof povdata.pip.sc === 'number') {
                    delete povdata.pip.sc;
                    povdata.pip.shc = povdata.pip.sc;
                    povdata.pip.she = povdata.pip.se;
                }
                // 18/04/2020
                // Now we have general pipeline parameters
                if (!povdata.env) povdata.env = {};
                const { pip, env } = povdata;
                const keys = Object.keys(pip);
                keys.forEach((key) => {
                    if (typeof pip[key] !== 'number') {
                        const stringDefault = JSON.stringify(defaultPipelineValues[key]);
                        if (pip[key] === undefined || JSON.stringify(pip[key]) === stringDefault) {
                            delete pip[key];
                        }
                    } else if (pip[key] === undefined || pip[key] === defaultPipelineValues[key]) {
                        delete pip[key];
                    }

                    if (environmentSkyValue[key] !== undefined) {
                        if (typeof environmentSkyValue[key] !== 'number') {
                            const stringDefault = JSON.stringify(environmentSkyValue[key]);
                            if (JSON.stringify(pip[key]) !== stringDefault) {
                                povdata.env[key] = pip[key];
                            }
                        } else if (pip[key] !== environmentSkyValue[key]) env[key] = pip[key];
                    }

                    // 28/05/2020
                    // ColorMapping managed in one array
                    if (pip.cod !== undefined) {
                        pip.cm = [pip.coh - 180, pip.cos, pip.coe, pip.cod];
                    }

                    if (env.sor && env.sor <= Math.PI && env.sor !== Math.round(env.sor)) {
                        env.sor = (env.sor * 180) / Math.PI;
                    }
                });

                // 05/11/2020
                // New radius option
                if (!povdata.rd) povdata.rd = 10;
            });
        }
    }
}
